import { Button, ButtonProps } from '@servicetitan/anvil2';
import { PropsWithChildren, MouseEvent } from 'react';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import {
    WorkflowStore,
    useProviderConfig,
    SetupDataStore,
    PrequalificationResponseStore,
} from '@servicetitan/financing-workflow-shared';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import { redirectToPostApplication } from '@servicetitan/financing-utils-shared';

export const DoneButton = ({
    appearance,
    children,
    style,
    ...buttonProps
}: PropsWithChildren<ButtonProps>) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { prequalificationResponse } = PrequalificationResponseStore.useStore();
    const { continueToHardAppAfterSoft } = useProviderConfig();
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const { setDecisionData } = DecisionDataStore.useStore();

    const handleClick = () => {
        // Check if we should transition to hard app (plan selection)
        const shouldContinueToHardApp =
            continueToHardAppAfterSoft &&
            prequalificationResponse?.prequalId != null &&
            !setupData?.hasTransitionedToHardApp;

        if (shouldContinueToHardApp) {
            // Set flags to indicate we've transitioned to hard app flow
            if (setupData) {
                setSetupData({
                    ...setupData,
                    hasTransitionedToHardApp: true,
                    isApplyingForPrequalification: false,
                });
            }
            setDecisionData({
                loanId: null,
                externalLoanId: null,
                decision: SubmitApplicationDecision.NotSet,
                planNumber: null,
                description: null,
                approvedAmount: null,
                term: null,
            });
            // Navigate to plan selection for hard app
            setWorkflowStage('transition-to-hard-app');
        } else {
            // Default behavior: redirect to post-application page
            const loanId = (decisionData?.loanId ?? '') || '0';
            redirectToPostApplication(loanId);
        }
    };

    const { onClick: externalOnClick, ...restButtonProps } = buttonProps;

    const combinedOnClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (externalOnClick) {
            externalOnClick(event);
        }

        handleClick();
    };

    return (
        <Button
            appearance={appearance ?? 'primary'}
            style={style}
            onClick={combinedOnClick}
            {...restButtonProps}
        >
            {children ?? 'Continue to Application'}
        </Button>
    );
};
